<template>
  <v-card outlined class="mb-4">
    <v-container fluid>
      <v-row>
        <v-col cols="12" sm="8">
          <v-list-item>
            <v-list-item-content class="refund-header-grid">
              <div class="refund-header-line">
                <span class="refund-header-label">{{ $t('refund.reference') }} :</span>
                <span class="refund-header-value">{{ refund.refund_id }}</span>
              </div>
              <div class="refund-header-line">
                <span class="refund-header-label">{{ $t('refund.numOrder') }} :</span>
                <router-link
                  v-if="refund.order_id"
                  class="refund-header-link"
                  :to="{ name: 'orderDetails', params: { id: refund.order_id } }"
                >
                  {{ refund.order_id }}
                </router-link>
                <span v-else class="refund-header-value">-</span>
              </div>
              <div v-if="refund.return_id" class="refund-header-line">
                <span class="refund-header-label">{{ $t('refund.numReturn') }} :</span>
                <router-link
                  class="refund-header-link"
                  :to="{ name: 'returnDetails', params: { id: refund.return_id } }"
                >
                  {{ refund.return_id }}
                </router-link>
              </div>
              <div v-if="customerName" class="refund-header-line">
                <span class="refund-header-label">{{ $t('refund.customer') }} :</span>
                <span class="refund-header-value">{{ customerName }}</span>
              </div>
              <div v-if="refund.customer && refund.customer.email" class="refund-header-line">
                <span class="refund-header-label">{{ $t('refund.email') }} :</span>
                <span class="refund-header-value">{{ refund.customer.email }}</span>
              </div>
              <div class="refund-header-line">
                <span class="refund-header-label">{{ $t('refund.status') }} :</span>
                <v-chip
                  v-if="hasStatusHistory"
                  small
                  :color="statusBackgroundColor"
                  :text-color="statusTextColor"
                  dark
                  @click="openHistoryDialog"
                >
                  {{ statusLabel }}
                  <v-icon :color="statusTextColor" x-small right>mdi-history</v-icon>
                </v-chip>
                <v-chip v-else small :color="statusBackgroundColor" :text-color="statusTextColor" dark>
                  {{ statusLabel }}
                </v-chip>
              </div>
            </v-list-item-content>
          </v-list-item>
        </v-col>
        <v-col cols="12" sm="4" class="text-sm-right">
          <v-list-item>
            <v-list-item-content>
              <div class="refund-header-line refund-header-line--right">
                <span class="refund-header-label">{{ $t('refund.date') }} :</span>
                <span class="refund-header-value" v-if="refund.created_at">
                  {{ new Date(refund.created_at) | moment('L LTS') }}
                </span>
                <span class="refund-header-value" v-else>-</span>
              </div>
              <div class="refund-header-line refund-header-line--right">
                <span class="refund-header-label">{{ $t('refund.products') }} :</span>
                <span class="refund-header-value">{{ nbProducts }}</span>
              </div>
              <div class="refund-header-line refund-header-line--right mt-2">
                <span class="refund-header-label">{{ $t('refund.amountRefunded') }} :</span>
                <span v-if="isAmountDefined(refund.amount)" class="refund-amount-ttc">
                  {{ refund.amount | formatPrice(refund.currency) }} {{ currencyCode }}
                  {{ $t('refund.taxIncludedShort') }}
                </span>
                <span v-else class="refund-amount-ttc">-</span>
              </div>
              <div v-if="isAmountDefined(refund.amount_exclude_tax)" class="refund-header-line refund-header-line--right">
                <span  class="refund-amount-ht">
                  {{ refund.amount_exclude_tax | formatPrice(refund.currency) }} {{ currencyCode }}
                  {{ $t('refund.taxExcludedShort') }}
                </span>
              </div>
            </v-list-item-content>
          </v-list-item>
        </v-col>
      </v-row>
    </v-container>

    <status-history-timeline
      v-model="showHistoryDialog"
      :history="refund.status_history || []"
      :title="$t('refund.statusHistory')"
      :get-label="getRefundStatusTranslation"
      :get-bg-color="getRefundStatusBackgroundColor"
      :get-text-color="getRefundStatusTextColor"
      @close="closeHistoryDialog"
    />
  </v-card>
</template>

<script>
  import { textExists } from '../../helpers/helper'
  import {
    getCurrencyCode,
    getRefundStatusBackgroundColor,
    getRefundStatusTextColor,
    getRefundStatusTranslation,
    isAmountDefined,
  } from '../../helpers/refundHelper'
  import StatusHistoryTimeline from '@/components/dialogs/StatusHistoryTimeline.vue'

  export default {
    name: 'RefundHeader',
    components: {
      StatusHistoryTimeline,
    },
    props: {
      refund: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        showHistoryDialog: false,
      }
    },
    computed: {
      customerName() {
        const customer = this.refund.customer
        if (!customer) {
          return ''
        }
        const fullName = [customer.first_name, customer.last_name].filter(Boolean).join(' ')
        if (fullName) {
          return fullName
        }
        return textExists(customer.customer_id) ? customer.customer_id : ''
      },
      currencyCode() {
        return getCurrencyCode(this.refund.currency)
      },
      nbProducts() {
        if (!Array.isArray(this.refund.items)) {
          return 0
        }
        return this.refund.items.reduce((sum, item) => sum + (item.refunded_quantity || 0), 0)
      },
      statusLabel() {
        return getRefundStatusTranslation(this.refund.current_status)
      },
      statusBackgroundColor() {
        return getRefundStatusBackgroundColor(this.refund.current_status)
      },
      statusTextColor() {
        return getRefundStatusTextColor(this.refund.current_status)
      },
      // The status chip becomes a history trigger only when there is a history to show.
      hasStatusHistory() {
        return Array.isArray(this.refund.status_history) && this.refund.status_history.length > 0
      },
    },
    methods: {
      isAmountDefined,
      // Exposed to the template so they can be passed as resolver props to StatusHistoryTimeline.
      getRefundStatusTranslation,
      getRefundStatusBackgroundColor,
      getRefundStatusTextColor,
      openHistoryDialog() {
        this.showHistoryDialog = true
      },
      closeHistoryDialog() {
        this.showHistoryDialog = false
      },
    },
  }
</script>

<style scoped>
  .refund-header-line {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 2px 0;
    font-size: 14px;
    /* When a "label : value" pair is too wide for its column, drop the value onto the next line
       rather than breaking the label itself mid-word (keeps the label intact whenever it fits). */
    flex-wrap: wrap;
  }
  .refund-header-line--right {
    justify-content: flex-end;
  }
  /* Below the sm breakpoint the two columns stack full-width; align the right-hand block to the
     left like the left column so the stacked rows share one consistent edge (instead of some
     rows flush-left and others flush-right). */
  @media (max-width: 599px) {
    .refund-header-line--right {
      justify-content: flex-start;
    }
  }
  .refund-header-label {
    text-transform: uppercase;
    font-size: 14px;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.6);
  }
  .refund-header-value {
    font-weight: 500;
  }
  .refund-header-link {
    text-decoration: none;
  }
  .refund-amount-ttc {
    font-weight: 700;
    color: var(--v-primary-base);
  }
  .refund-amount-ht {
    color: rgba(0, 0, 0, 0.6);
  }
</style>
